/**
 * AML URL Mapping Utilities
 *
 * Centralized URL building and validation for AML navigation
 * Follows the same pattern as compliance-url-mapping.ts
 */

/**
 * Valid AML issue types (K-U continuation from Compliance A-J)
 */
const VALID_AML_ISSUE_TYPES = ['all', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U'] as const;

export type AMLIssueTypeParam = typeof VALID_AML_ISSUE_TYPES[number];

/**
 * Centralized URL building function for AML navigation
 * Handles client/entity context and provides fallbacks for missing data
 */
export function buildAMLUrl(
  issueType: string,
  clientId: string | null,
  entityId: string | 'all' | null
): string {
  if (!clientId) {
    console.warn('buildAMLUrl: No clientId provided, redirecting to dashboard');
    return '/dashboard'; // Fallback if no client selected
  }

  // Validate issue type and use default if invalid
  const validatedIssueType = isValidAMLIssueType(issueType) ? issueType : 'all';

  // Handle entity parameter - empty string for 'all entities'
  const entityParam = entityId && entityId !== 'all' ? entityId : '';

  return `/aml/${validatedIssueType}/${clientId}/${entityParam}`;
}

/**
 * Gets the page title based on AML issue type
 * Aligned with AML_ISSUE_TYPE_INFO in aml.types.ts
 */
export function getAMLPageTitle(issueType: string): string {
  switch (issueType?.toUpperCase()) {
    case 'K':
      return 'New Counterparty';
    case 'L':
      return 'Contact Spikes';
    case 'M':
      return 'Soft GL Usage';
    case 'N':
      return 'Missing Attachments';
    case 'O':
      return 'Volume Spikes';
    case 'P':
      return 'Structuring';
    case 'Q':
      return 'Pass-Through';
    case 'R':
      return 'High Credit Note Ratio';
    case 'S':
      return 'Round Numbers';
    case 'T':
      return 'Backdated';
    case 'U':
      return 'Shared Bank';
    case 'ALL':
    default:
      return 'All AML Issues';
  }
}

/**
 * Gets the description for an AML issue type
 * Aligned with AML_ISSUE_TYPE_INFO in aml.types.ts
 */
export function getAMLIssueDescription(issueType: string): string {
  switch (issueType?.toUpperCase()) {
    case 'K':
      return 'First-time supplier or customer with large transaction';
    case 'L':
      return 'Unusual increase in supplier/customer count';
    case 'M':
      return 'Large transactions in suspense/clearing accounts';
    case 'N':
      return 'High-value transactions without supporting documents';
    case 'O':
      return 'Unusual increase in spend/revenue volume';
    case 'P':
      return 'Multiple similar-amount transactions within 72h suggesting intentional splitting';
    case 'Q':
      return 'Money flows in then out within 7 days with similar amounts';
    case 'R':
      return 'Contacts with excessive credit notes (>15%)';
    case 'S':
      return 'Suspiciously round-number invoices or duplicate references';
    case 'T':
      return 'Transactions entered more than 3 days after their date';
    case 'U':
      return 'Multiple suppliers receiving payments to the same bank account';
    case 'ALL':
    default:
      return 'All AML fraud detection issues';
  }
}

/**
 * Gets the breadcrumb text based on AML issue type
 * Aligned with AML_ISSUE_TYPE_INFO in aml.types.ts
 */
export function getAMLBreadcrumb(issueType: string): string {
  switch (issueType?.toUpperCase()) {
    case 'K':
      return 'New Counterparty';
    case 'L':
      return 'Contact Spikes';
    case 'M':
      return 'Soft GL Usage';
    case 'N':
      return 'Missing Attachments';
    case 'O':
      return 'Volume Spikes';
    case 'P':
      return 'Structuring';
    case 'Q':
      return 'Pass-Through';
    case 'R':
      return 'Credit Note Ratio';
    case 'S':
      return 'Round Numbers';
    case 'T':
      return 'Backdated';
    case 'U':
      return 'Shared Bank';
    case 'ALL':
    default:
      return 'All Issues';
  }
}

/**
 * Validates if an AML issue type is supported
 */
export function isValidAMLIssueType(issueType: string): boolean {
  return VALID_AML_ISSUE_TYPES.includes(issueType?.toUpperCase() as AMLIssueTypeParam);
}

/**
 * Gets default issue type for fallback
 */
export function getDefaultAMLIssueType(): string {
  return 'all';
}
